/* eslint-disable react/no-unknown-property */
import React from 'react';
import {
  ActionIcon,
  Badge,
  DetailStack,
  HStack,
  ICON_ACCOUNT_OFF_OUTLINE,
  ICON_BRIEFCASE_OUTLINE,
  ICON_EYE_OUTLINE,
  Popover,
  SimpleTable,
  Stack,
  Text,
  Tooltip,
  UserPreview,
} from '@doveit/bricks';
import { isFuture } from 'date-fns';
import { pluralizeWord } from '@doveit/hammer';
import {
  HRInterview,
  HRRecall,
  JobApplicationSales,
  SerpJobApplication,
} from '../../../providers/api/dtos';
import { JobApplicationType } from '../../../domain/types';
import { NO_VALUE_SYMBOL } from '../../../property/utils';
import DateViewer from '../../../components/date-viewer/DateViewer';
import TextPreview from '../../../components/text-preview/TextPreview';
import UpdateJobApplicationStatus from '../update-job-application-status/UpdateJobApplicationStatus';
import UpdateHRInterviewStatus from '../../../hr/containers/update-hr-interview-status/UpdateHRInterviewStatus';
import UpdateHRRecallStatus from '../../../hr/containers/update-hr-recall-status/UpdateHRRecallStatus';
import ViewHRInterviewPopoverPreview from '../../../hr/containers/view-hr-interview-popover-preview/ViewHRInterviewPopoverPreview';
import * as styles from './ViewSerpJobApplicationTableRow.style';
import { ThemeIcons } from '../../../theme/icons';
import { ViewAgentSmallPreview } from '../../../agent/components';
import ApplicantMissedCallRegistryList from '../../components/applicant-missed-call-registry-list/ApplicantMissedCallRegistryList';
import ViewApplicantPopoverPreview from '../view-applicant-popover-preview/ViewApplicantPopoverPreview';
import { useJobApplicationSerpData } from '../../hooks/use-job-application-serp-data/useJobApplicationSerpData';
import { setVerticalEllipsis } from '../../../utils/styles/verticalEllipsis.style';

export interface ViewSerpJobApplicationTableRowProps extends React.AriaAttributes {
  serpJobApplication: SerpJobApplication,
  onJobApplicationUpdate?: VoidFunction,
  onInterviewUpdate?: (updatedInterview: HRInterview) => void,
  onRecallUpdate?: (updatedRecall: HRRecall) => void,
}

const ViewSerpJobApplicationTableRow: React.FC<ViewSerpJobApplicationTableRowProps> = ({
  serpJobApplication,
  onJobApplicationUpdate,
  onInterviewUpdate,
  onRecallUpdate,
  ...rest
}) => {
  const {
    jobApplication,
    applicant,
    jobPositionData,
    calendarEvent,
    note,
    showMissedCalls,
    supervisorName,
    supervisorPicture,
    assigneeName,
    goToJobApplicationPage,
  } = useJobApplicationSerpData(serpJobApplication);

  const { isFranchising, provinces } = React.useMemo(() => {
    if (jobApplication.applicationType === JobApplicationType.SALES) {
      const salesJobApplication = jobApplication as JobApplicationSales;
      return {
        isFranchising: salesJobApplication.isFranchising,
        provinces: salesJobApplication.provinces,
      };
    }
    return {
      isFranchising: undefined,
      provinces: undefined,
    };
  }, [jobApplication]);

  return (
    <SimpleTable.Row {...rest}>

      <SimpleTable.Cell
        aria-label="Candidato"
        width="10rem"
      >
        <ViewApplicantPopoverPreview
          applicantId={jobApplication.applicantId}
          isFranchising={isFranchising}
          trigger={(
            <div css={styles.applicantWrapper}>
              <UserPreview
                avatar={{ placeholder: ICON_BRIEFCASE_OUTLINE, icon: !isFranchising ? ThemeIcons.DOVE : undefined }}
                size="S"
                name={applicant?.name}
              >
                {applicant?.phoneNumber || applicant?.email}
              </UserPreview>
            </div>
          )}
        />

      </SimpleTable.Cell>

      <SimpleTable.Cell
        aria-label="Posizione"
        width="7rem"
      >
        <DetailStack label="Posizione">
          <Tooltip content={jobPositionData.data?.name ?? 'Non presente'}>
            <div css={setVerticalEllipsis(1)}>
              {jobPositionData.data?.name ?? NO_VALUE_SYMBOL}
            </div>
          </Tooltip>
        </DetailStack>
      </SimpleTable.Cell>

      <SimpleTable.Cell aria-label="Provincia">
        {(!provinces || provinces.length === 0) && (
          <DetailStack label="Provincia">
            {NO_VALUE_SYMBOL}
          </DetailStack>
        )}
        {provinces && provinces.length === 1 && (
          <DetailStack label="Provincia">
            <Tooltip content={provinces[0]}>
              <div css={setVerticalEllipsis(1)}>
                {provinces[0]}
              </div>
            </Tooltip>
          </DetailStack>
        )}
        {provinces && provinces.length > 1 && (
          <Popover
            aria-label="Visualizza tutte le province"
            trigger={(
              <DetailStack label="Province">
                <div css={setVerticalEllipsis(1)}>
                  {`${provinces[0]},+${provinces.length - 1}`}
                </div>
              </DetailStack>
            )}
            hasArrow
          >
            <div css={styles.provincesPopoverWrapper}>
              <Stack gap={75}>
                {provinces.map((province) => (
                  <Text.Caption key={province}>{province}</Text.Caption>
                ))}
              </Stack>
            </div>
          </Popover>
        )}
      </SimpleTable.Cell>

      <SimpleTable.Cell aria-label="Stato candidatura">
        <UpdateJobApplicationStatus
          jobApplication={jobApplication}
          size="XS"
          onSuccess={onJobApplicationUpdate}
        />
      </SimpleTable.Cell>

      <SimpleTable.Cell aria-label="Gestori">
        <Popover
          aria-label="Anteprima gestori candidatura"
          trigger={(
            <div css={styles.managersPreviewWrapper}>
              <UserPreview
                size="S"
                aria-label={supervisorName ?? 'Gestore HR non assegnato'}
                // eslint-disable-next-line no-nested-ternary
                avatar={supervisorName ? (supervisorPicture ? { src: supervisorPicture } : undefined) : { placeholder: ICON_ACCOUNT_OFF_OUTLINE }}
              />
              {jobApplication.assigneeEmail && jobApplication.applicationType === JobApplicationType.SALES && (
                <ViewAgentSmallPreview
                  agentIdOrEmail={jobApplication.assigneeEmail}
                  aria-label="Anteprima assegnatario"
                  size="S"
                  omit={{ name: true }}
                />
              )}
              {(!jobApplication.assigneeEmail || jobApplication.applicationType !== JobApplicationType.SALES) && (
                <UserPreview
                  size="S"
                  aria-label={jobApplication.assigneeEmail ?? 'Assegnatario non definito'}
                  avatar={jobApplication.assigneeEmail ? undefined : { placeholder: ICON_ACCOUNT_OFF_OUTLINE }}
                />
              )}
            </div>
          )}
          hasArrow
        >
          <div css={styles.managersPopoverWrapper}>
            <Stack gap={100}>
              <UserPreview
                name={supervisorName ?? 'Non assegnato'}
                size="S"
                aria-label="Gestore HR"
                avatar={supervisorPicture ? { src: supervisorPicture } : undefined}
              >
                Gestore HR
              </UserPreview>
              {jobApplication.assigneeEmail && jobApplication.applicationType === JobApplicationType.SALES && (
                <ViewAgentSmallPreview
                  size="S"
                  agentIdOrEmail={jobApplication.assigneeEmail}
                  aria-label="Assegnatario"
                  details="Assegnatario"
                />
              )}
              {jobApplication.assigneeEmail && jobApplication.applicationType !== JobApplicationType.SALES && (
                <UserPreview
                  name={assigneeName ?? 'Non assegnato'}
                  size="S"
                  aria-label="Assegnatario"
                >
                  Assegnatario
                </UserPreview>
              )}
              {!jobApplication.assigneeEmail && (
                <UserPreview
                  name="Non assegnato"
                  size="S"
                  aria-label="Assegnatario"
                >
                  Assegnatario
                </UserPreview>
              )}
            </Stack>
          </div>
        </Popover>
      </SimpleTable.Cell>

      <SimpleTable.Cell aria-label="Data creazione">
        <DetailStack label="Aggiunta">
          <DateViewer
            stringDate={jobApplication.createdAt!}
            checkIfToday
          />
        </DetailStack>
      </SimpleTable.Cell>

      <SimpleTable.Cell
        withDivider={!!calendarEvent || !!note}
      >
        <HStack
          gap={200}
          wrap="nowrap"
        >
          {calendarEvent && (
            <>
              {calendarEvent.type === 'RECALL' ? (
                <DetailStack
                  aria-label="Evento a calendario"
                  label="Recall"
                  width="7rem"
                >
                  <DateViewer
                    stringDate={calendarEvent.date}
                    checkIfToday
                    checkIfTomorrow
                    withHour={isFuture(new Date(calendarEvent.date))}
                  />
                </DetailStack>
              ) : (
                <ViewHRInterviewPopoverPreview
                  hrInterview={calendarEvent as HRInterview}
                  applicationType={jobApplication.applicationType}
                  applicantId={applicant.id!}
                  aria-label="Visualizza dettagli colloquio"
                  trigger={(
                    <DetailStack
                      label={(calendarEvent as HRInterview).isRemote ? 'Colloquio da remoto' : 'Colloquio di presenza'}
                      aria-label="Evento a calendario"
                      width="7rem"
                    >
                      <DateViewer
                        stringDate={calendarEvent.date}
                        checkIfToday
                        checkIfTomorrow
                        withHour
                      />
                    </DetailStack>
                  )}
                />
              )}
              {calendarEvent.type === 'RECALL' ? (
                <UpdateHRRecallStatus
                  hrRecall={calendarEvent as HRRecall}
                  onSuccess={onRecallUpdate}
                />
              ) : (
                <UpdateHRInterviewStatus
                  hrInterview={calendarEvent as HRInterview}
                  onSuccess={onInterviewUpdate}
                />
              )}
            </>
          )}

          {note && (
            <TextPreview
              aria-label="Note"
              text={note.text}
              author={note.author}
              date={note.date}
            />
          )}
        </HStack>
      </SimpleTable.Cell>

      <SimpleTable.Cell
        aria-label="Azioni"
        align="right"
      >
        <HStack gap={75} wrap="nowrap">
          {showMissedCalls && (
            <div
              aria-label="Contatore chiamate senza risposta"
              css={styles.missedCallsBadgeWrapper}
            >
              {applicant.missedCalls.counter === 0 && (
                <Badge
                  label={applicant.missedCalls.counter.toString()}
                  size="XS"
                  color="primary"
                  aria-label={`${applicant.missedCalls.counter} chiamate senza risposta`}
                />
              )}
              {applicant.missedCalls.counter > 0 && (
                <Popover
                  aria-label="Visualizza chiamate senza risposta"
                  trigger={(
                    <Badge
                      label={applicant.missedCalls.counter.toString()}
                      size="XS"
                      color="neutral"
                      aria-label={`${applicant.missedCalls.counter} ${pluralizeWord('chiamat', 'a', 'e', applicant.missedCalls.counter)} senza risposta`}
                    />
                  )}
                  hasArrow
                >
                  <div css={styles.missedCallRegistryPopoverWrapper}>
                    <ApplicantMissedCallRegistryList missedCalls={applicant.missedCalls} />
                  </div>
                </Popover>
              )}
            </div>
          )}
          <ActionIcon
            aria-label="Visualizza candidatura"
            label="Visualizza"
            size="S"
            icon={{ path: ICON_EYE_OUTLINE }}
            onClick={goToJobApplicationPage}
          />
        </HStack>
      </SimpleTable.Cell>
    </SimpleTable.Row>
  );
};

export default ViewSerpJobApplicationTableRow;
